<script lang="ts" setup>
import PhoneInput, { PhoneInputProps } from '@/molecules/PhoneInput.vue';
import TextInput, { TextInputProps } from '@/molecules/TextInput.vue';
import TextArea, { TextAreaProps } from '@/molecules/TextArea.vue';
import { BaseCheckboxProps } from '@/atoms/BaseCheckbox.vue';
import BaseButton, { BaseButtonProps } from '@/atoms/BaseButton.vue';
import BaseLabel, { BaseLabelProps } from '@/atoms/BaseLabel.vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { AnySchema } from 'yup';
import defaultValidators from '@/helpers/default-validators';
import { computed, ref } from 'vue';
import CheckboxInput from '@/molecules/CheckboxInput.vue';
import ToggleInput from '@/molecules/ToggleInput.vue';
import SelectInput, { SelectInputProps } from '@/molecules/SelectInput.vue';
import BottomButtons from '@/molecules/BottomButtons.vue';
import { cn } from '@/helpers/tailwind-helpers';
import GooglePlaceAutocompleteInput, {
  GooglePlaceAutocompleteInputProps,
} from '@/molecules/GooglePlaceAutocompleteInput.vue';
import CheckboxRowItem from '@/molecules/CheckboxRowItem.vue';
import { type EnterKeyHint, NAVIGABLE_INPUT_TYPES, resolveEnterKeyHint } from '@/constant/inputs';
import PasswordInput from '@/molecules/PasswordInput.vue';
import { BaseTabProps } from '@/molecules/BaseTab.vue';
import BaseTabInput from '@/molecules/BaseTabInput.vue';
import BaseAlert from '@/atoms/BaseAlert.vue';
import { AppError, ErrorDisplay } from '@/definitions/error';
import HighlightBox from '@/molecules/HighlightBox.vue';
import RadioRowItem from '@/molecules/RadioRowItem.vue';
import RowItemContainer, { ClickableRowItemProps } from '@/organisms/RowItemContainer.vue';

export type BaseFieldSchema = {
  id: string;
  rules?: AnySchema;
  initialTouched?: boolean;
  initialValue?: any;
  maxLength?: number;
  enterkeyhint?: EnterKeyHint;
  highlight?: {
    value?: string | number;
    suffix?: string;
    text?: string;
    class?: string;
  };
  warning?: string | ((value: any) => string | undefined);
};

export type TextSchema = BaseFieldSchema & {
  type: 'text' | 'number' | 'email' | 'password';
  as: typeof TextInput;
} & TextInputProps;

export type PasswordSchema = BaseFieldSchema & {
  type: 'password';
  as: typeof PasswordInput;
} & TextInputProps;

export type TextAreaSchema = BaseFieldSchema & {
  type: 'textarea';
  as: typeof TextArea;
} & TextAreaProps;

export type SelectSchema = BaseFieldSchema & {
  type: 'text' | 'item';
  as: typeof SelectInput;
} & SelectInputProps;

export type BaseTabSchema = BaseFieldSchema & {
  type: 'text';
  as: typeof BaseTabInput;
} & BaseTabProps;

export type PhoneSchema = BaseFieldSchema & {
  type: 'tel';
  as: typeof PhoneInput;
} & PhoneInputProps;

export type CheckboxSchema = BaseFieldSchema & {
  type: 'checkbox';
  as: typeof CheckboxInput | typeof CheckboxRowItem | typeof ToggleInput;
} & BaseCheckboxProps;

export type AutocompleteSchema = BaseFieldSchema & {
  type: 'autocomplete';
  as: typeof GooglePlaceAutocompleteInput;
} & GooglePlaceAutocompleteInputProps;

export type DescriptionSchema = {
  type: 'description';
  as: typeof BaseLabel;
} & BaseLabelProps;

export type RadioGroupSchema = BaseFieldSchema & {
  type: 'radio-group';
  as: typeof RadioRowItem;
  options: any[]; // patients ici
  optionValue: string; // 'id'
  optionLabel: (option: any) => string;
  optionDescription?: (option: any) => string;
};

export type FieldSchema =
  | SelectSchema
  | TextSchema
  | TextAreaSchema
  | PasswordSchema
  | BaseTabSchema
  | PhoneSchema
  | CheckboxSchema
  | AutocompleteSchema
  | RadioGroupSchema
  | DescriptionSchema;

export type InputFieldSchema = Exclude<FieldSchema, DescriptionSchema>;

export type FormSchema = {
  fields: FieldSchema[];
  validateOnChange: boolean;
  validateOnBlur?: boolean;
};

export type DynamicFormProps = {
  schema: FormSchema;
  submitButtonProps?: Omit<BaseButtonProps, 'onClick'>;
  onSubmit?: (values: any) => Promise<any> | void;
  class?: string;
};

const props = defineProps<DynamicFormProps>();

function isInputField(field: FieldSchema): field is InputFieldSchema {
  return field.type !== 'description';
}

const onFormEnter = (e: KeyboardEvent) => {
  const target = e.target as HTMLInputElement;
  if (target.tagName !== 'INPUT' || !NAVIGABLE_INPUT_TYPES.has(target.type)) {
    return;
  }

  e.preventDefault();

  const form = e.currentTarget as HTMLFormElement;
  const inputs = Array.from(form.querySelectorAll<HTMLInputElement>('input')).filter(
    (input) => !input.disabled && NAVIGABLE_INPUT_TYPES.has(input.type),
  );
  const currentIndex = inputs.indexOf(target);

  if (currentIndex >= 0 && currentIndex < inputs.length - 1) {
    inputs[currentIndex + 1].focus();
  } else {
    target.blur();
  }
};

function buildValidationSchema(schema: FormSchema) {
  const shape: Record<string, AnySchema> = {};
  for (const field of schema.fields) {
    if (isInputField(field)) {
      shape[field.id] = resolveRules(field);
    }
  }
  return yup.object(shape);
}

const form = useForm({
  validationSchema: buildValidationSchema(props.schema),
});

const { errors, defineField, validateField, meta, values, validate, resetForm } = form;

const fields = computed(() => {
  let lastNavigableIndex = -1;
  props.schema.fields.forEach((field, idx) => {
    if (isInputField(field) && NAVIGABLE_INPUT_TYPES.has(field.type)) {
      lastNavigableIndex = idx;
    }
  });

  return props.schema.fields.map((fieldSchema, index) => {
    if (!isInputField(fieldSchema)) {
      // For description fields, separate component props from type/as
      const { type, as, ...componentProps } = fieldSchema;
      return {
        _type: 'description' as const,
        type,
        as,
        componentProps,
      };
    }

    const validationSchema = resolveRules(fieldSchema);
    const required = isFieldRequired(validationSchema);
    const [field, fieldProps] = defineField(fieldSchema.id, {
      validateOnBlur: props.schema.validateOnBlur ?? true,
      validateOnChange: props.schema.validateOnChange,
      validateOnInput: props.schema.validateOnChange,
      validateOnModelUpdate: props.schema.validateOnChange,
    });

    if (fieldSchema.initialValue !== undefined && fieldSchema.initialValue !== null) {
      field.value = fieldSchema.initialValue;
    }

    if (isInputField(fieldSchema) && fieldSchema.initialTouched) {
      validateField(fieldSchema.id);
    }

    // Exclude internal properties that shouldn't be passed to components
    const {
      id,
      type,
      as,
      rules,
      initialTouched,
      initialValue,
      enterkeyhint,
      warning,
      ...componentProps
    } = fieldSchema;

    const resolevedEnterkeyhint = resolveEnterKeyHint(
      type,
      index === lastNavigableIndex,
      enterkeyhint,
    );

    return {
      _type: 'input' as const,
      id,
      type,
      as,
      field,
      fieldProps,
      rules,
      initialTouched,
      initialValue,
      componentProps: {
        ...componentProps,
        label:
          required && componentProps?.label ? `${componentProps?.label} *` : componentProps?.label,
      },
      enterkeyhint: resolevedEnterkeyhint,
      highlight: fieldSchema.highlight,
      warning: fieldSchema.warning,
      radioOptions:
        fieldSchema.type === 'radio-group'
          ? {
              options: fieldSchema.options as Option[],
              optionValue: fieldSchema.optionValue,
              optionLabel: fieldSchema.optionLabel as (option: Option) => string,
              optionDescription: fieldSchema.optionDescription as
                | ((option: Option) => string)
                | undefined,
            }
          : undefined,
    };
  });
});

function isFieldRequired(schema: AnySchema): boolean {
  const fieldDescription = schema?.describe();

  const checkFieldIsRequired = (field) =>
    field.tests?.some((test) => test.name === 'required') || field.optional === false;
  if (fieldDescription.fields) {
    return Object.values(fieldDescription.fields).some((field) => checkFieldIsRequired(field));
  }

  return checkFieldIsRequired(fieldDescription);
}

function resolveRules(field: InputFieldSchema): AnySchema {
  const base = defaultValidators[field.type];

  if (!field.rules) {
    return base;
  }

  return field.rules;
}

// Warnings resolved separately so they never trigger defineField re-runs
const resolvedWarnings = computed(() => {
  const result: Record<string, string | undefined> = {};
  for (const fieldSchema of props.schema.fields) {
    if (!isInputField(fieldSchema) || !fieldSchema.warning) continue;
    const value = values[fieldSchema.id];
    result[fieldSchema.id] =
      typeof fieldSchema.warning === 'function' ? fieldSchema.warning(value) : fieldSchema.warning;
  }
  return result;
});

const classes = computed(() => cn('space-y-3 justify-between h-full', props.class));

defineExpose({ values, errors, meta, validate, resetForm, form });

const isLoading = ref(false);
const errorDisplay = ref<ErrorDisplay | null>(null);

const handleSubmit = async () => {
  const { valid } = await validate({ mode: 'force' });
  if (valid) {
    isLoading.value = true;
    try {
      await props.onSubmit?.(values);
    } catch (error) {
      console.error(error);
      if (error instanceof AppError) {
        errorDisplay.value = error.display;
      } else {
        errorDisplay.value = {
          title: 'Une erreur est survenue lors de votre demande, veuillez réessayer plus tard',
        };
      }
    } finally {
      isLoading.value = false;
    }
  }
};

export type Option = {
  [key: string]: any;
  optionDescription: string;
};

type RadioOptions = {
  options: Option[];
  optionValue: string;
  optionLabel: (option: Option) => string;
  optionDescription?: (option: Option) => string;
};

const mapOptionToRowItem = (opts: RadioOptions): ClickableRowItemProps[] => {
  return opts.options.map((option) => ({
    id: option[opts.optionValue],
    value: option[opts.optionValue],
    title: opts.optionLabel(option),
    class: 'py-7',
    description: opts.optionDescription ? opts.optionDescription(option) : undefined,
  }));
};
</script>

<template>
  <form :class="classes" @submit.prevent @keydown.enter="onFormEnter">
    <div class="flex flex-wrap w-full space-y-6">
      <template v-for="(item, index) in fields" :key="index">
        <BaseLabel
          v-if="item._type === 'description'"
          class="w-full"
          v-bind="item.componentProps"
        />
        <template v-else>
          <div v-if="item.type === 'radio-group'" class="flex flex-col space-y-2 h-full w-full">
            <BaseLabel
              :text="item.componentProps.label!"
              color="secondary"
              preset="medium"
              weight="regular"
            />

            <!-- highlight -->
            <HighlightBox v-if="item.highlight" v-bind="item.highlight" />

            <div class="space-y-2 w-full">
              <RowItemContainer
                v-model="item.field.value"
                :class="[
                  'border-x border-green-600',
                  index === 0 && 'border-t rounded-t-xl',
                  index === item.componentProps.options.length - 1 && 'border-b rounded-b-xl',
                  index !== item.componentProps.options.length - 1 && 'border-b border-green-600',
                ]"
                :row-items="mapOptionToRowItem(item.radioOptions!)"
              />
            </div>

            <BaseLabel
              v-if="errors[item.id]"
              :data-cy="`error-${item.id}`"
              :text="errors[item.id]"
              color="error"
              preset="small"
            />
          </div>

          <!-- 👇 IMPORTANT -->
          <Component
            :is="item.as"
            v-else
            :id="item.id"
            v-model="item.field.value"
            :destructive="!!errors[item.id] || !!resolvedWarnings[item.id]"
            :enterkeyhint="item.enterkeyhint"
            :error="errors[item.id]"
            :field-props="item.fieldProps.value"
            :hint="!errors[item.id] ? resolvedWarnings[item.id] : undefined"
            :type="item.type"
            class="w-full"
            v-bind="item.componentProps"
          />
        </template>
      </template>
    </div>

    <BaseAlert
      v-if="errorDisplay"
      :description="errorDisplay.description"
      :title="errorDisplay.title"
      type="error"
    />
  </form>

  <slot />

  <BottomButtons v-if="submitButtonProps" class="justify-end">
    <BaseButton
      id="submit"
      :on-click="handleSubmit"
      :state="isLoading ? 'loading' : 'default'"
      class="mt-4 w-full"
      v-bind="submitButtonProps"
    />
  </BottomButtons>
</template>
